import { createReducer, on } from "@ngrx/store";
import { DateHelper } from "src/components/shared-components/class/general-classes/date-helper";
import { DateTimeFormats } from "src/components/shared-components/class/general-classes/date-time-formats";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { Scenario } from "src/components/shared-components/class/general-classes/scenario";
import {
  scenarioDeletedAction,
  scenarioInsertedAction,
  scenarioRunningChangedAction,
  scenarioScheduleAssignedAction,
  scenarioScheduleUnassignedAction,
  scenariosLoadErrorAction,
  scenariosLoadInitStartAction,
  scenariosLoadSuccessAction,
  scenariosUnsetKeyStorageAction,
  scenarioUpdatedAction,
  scheduleDeletedFromScenariosAction,
} from "../actions/scenario.action";

/**
 * Scenario feature state: extends the shared "loaded/data" envelope with the
 * HTTP status code of the last failed scenario API call (-1 means no error).
 */
export interface ScenariosState extends GenericStoreApiData {
  errorCode: number;
}

/**
 * Initial state of scenarios store
 */
const initialState: ScenariosState = {
  loaded: false,
  errorGeneric: false,
  errorNotYetImplemented: false,
  isOldDataInUse: false,
  data: null,
  errorCode: -1,
};

const returnLastModifiedData = (apiData: GenericApiData): GenericApiData => ({
  ...apiData,
  lastAPIModified: DateHelper.format(new Date(), DateTimeFormats.DATETIME_FORMAT),
});

/**
 * Reducer for scenarios
 */
export const ScenarioReducer = createReducer(
  initialState,
  on(
    scenariosUnsetKeyStorageAction,
    () => {
      return initialState;
    }
  ),
  on(
    scenariosLoadInitStartAction,
    (scenarios: ScenariosState, action) => {
      return { ...scenarios, loaded: false };
    }
  ),
  on(scenariosLoadSuccessAction, (scenariosObject: ScenariosState, action) => {
    return {
      loaded: true,
      errorGeneric: false,
      errorNotYetImplemented: false,
      isOldDataInUse: false,
      data: action.scenarioObject,
      errorCode: -1,
    };
  }),
  on(scenariosLoadErrorAction, (scenariosObject: ScenariosState, action) => {
    return {
      loaded: true,
      errorGeneric: true,
      errorNotYetImplemented: false,
      isOldDataInUse: false,
      data: null,
      errorCode: action.errorCode,
    };
  }),
  on(scenarioInsertedAction, (state: ScenariosState, { scenario }) => {
    if (!state.data) {
      return state;
    }
    if (state.data.data.some((item: Scenario) => item.id === scenario.id)) {
      return state;
    }
    const nextList = [...state.data.data, scenario].sort(
      (a: Scenario, b: Scenario) => a.name.localeCompare(b.name)
    );
    return {
      ...state,
      data: {
        ...returnLastModifiedData(state.data),
        data: nextList,
      },
    };
  }),
  on(scenarioUpdatedAction, (state: ScenariosState, { scenario }) => {
    if (!state.data) {
      return state;
    }
    const index = state.data.data.findIndex(
      (item: Scenario) => item.id === scenario.id
    );
    if (index < 0) {
      return state;
    }
    const nextList = [...state.data.data];
    nextList[index] = scenario;
    nextList.sort((a: Scenario, b: Scenario) =>
      a.name.localeCompare(b.name)
    );
    return {
      ...state,
      data: {
        ...returnLastModifiedData(state.data),
        data: nextList,
      },
    };
  }),
  on(scenarioDeletedAction, (state: ScenariosState, { idScenario }) => {
    if (!state.data) {
      return state;
    }
    return {
      ...state,
      data: {
        ...returnLastModifiedData(state.data),
        data: state.data.data.filter(
          (item: Scenario) => item.id !== idScenario
        ),
      },
    };
  }),
  on(
    scenarioScheduleAssignedAction,
    (state: ScenariosState, { idScenario, idBigLogic }) => {
      if (!state.data) {
        return state;
      }
      const index = state.data.data.findIndex(
        (item: Scenario) => item.id === idScenario
      );
      if (index < 0) {
        return state;
      }
      const target: Scenario = state.data.data[index];

      /** Guard against duplicates */
      if (target.scheduleIDs?.includes(idBigLogic)) {
        return state;
      }

      const newList = [...state.data.data];
      newList[index] = {
        ...target,
        scheduleIDs: [...(target.scheduleIDs || []), idBigLogic],
      } as Scenario;
      return {
        ...state,
        data: {
          ...returnLastModifiedData(state.data),
          data: newList,
        },
      };
    }
  ),
  on(
    scenarioScheduleUnassignedAction,
    (state: ScenariosState, { idScenario, idBigLogic }) => {
      if (!state.data) {
        return state;
      }
      const index = state.data.data.findIndex(
        (item: Scenario) => item.id === idScenario
      );
      if (index < 0) {
        return state;
      }
      const target: Scenario = state.data.data[index];

      const newList = [...state.data.data];
      newList[index] = {
        ...target,
        scheduleIDs: target.scheduleIDs.filter((id) => id !== idBigLogic),
      } as Scenario;
      return {
        ...state,
        data: {
          ...returnLastModifiedData(state.data),
          data: newList,
        },
      };
    }
  ),
  on(
    scenarioRunningChangedAction,
    (state: ScenariosState, { idScenario, running }) => {
      if (!state.data) {
        return state;
      }
      const index = state.data.data.findIndex(
        (item: Scenario) => item.id === idScenario
      );
      if (index < 0) {
        return state;
      }
      const target: Scenario = state.data.data[index];

      const newList = [...state.data.data];
      newList[index] = { ...target, running } as Scenario;
      return {
        ...state,
        data: {
          ...returnLastModifiedData(state.data),
          data: newList,
        },
      };
    }
  ),
  on(
    scheduleDeletedFromScenariosAction,
    (state: ScenariosState, { idBigLogic }) => {
      if (!state.data) {
        return state;
      }
      const newList = state.data.data.map((scenario: Scenario) => {
        if (scenario.scheduleIDs?.includes(idBigLogic)) {
          return {
            ...scenario,
            scheduleIDs: scenario.scheduleIDs.filter((id) => id !== idBigLogic),
          } as Scenario;
        }
        return scenario;
      });
      return {
        ...state,
        data: {
          ...returnLastModifiedData(state.data),
          data: newList,
        },
      };
    }
  )
);
